import React, { useState, useEffect, useCallback } from 'react';
import { useReadCypher } from 'use-neo4j';
import 'bootstrap/dist/css/bootstrap.min.css';
import ForceGraph2D from 'react-force-graph-2d';
import { reTraduccion, traducirRelacion } from './components/tools';

const typesList = [
  {
    relation: 'Pelicula',
    color: '#3A6FC3B7'
  },
  {
    relation: 'Actor/Actriz',
    color: '#3AC34CB7'
  },
  {
    relation: 'Dirigido/a',
    color: '#C33A3AB7'
  },
  {
    relation: 'Producido/a',
    color: '#AC3AC3B7'
  },
  {
    relation: 'Escritor',
    color: '#C3B83AB7'
  },
  {
    relation: 'Seguimientos',
    color: '#C3853AB7'
  },
  {
    relation: 'Revisiones',
    color: '#C33AAAB7'
  },
];

const App = () => {
  const [rows, setRows] = useState<any>([]);
  const [personSelected, setPersonSelected] = useState<string>('');
  const [movieSelected, setMovieSelected] = useState<string>('');
  const [relationshipSelected, setRelationshipSelected] = useState<string>('');
  const [yearSelected, setYearSelected] = useState<string>('');
  const [orderPersonSelected, setOrderPersonSelected] = useState<string>('');
  const [orderMovieSelected, setOrderMovieSelected] = useState<string>('');
  const [queryShow, setQueryShow] = useState<string>(`
MATCH (p:Person)-[d]->(m:Movie)
RETURN p, d, m`
  );
  const [dataGraph, setDataGraph] = useState<any>({ nodes: [], links: [] });

  const { loading, result: queryresult, run, error } = useReadCypher(`
MATCH (p:Person)-[d]->(m:Movie)
  WHERE (COALESCE($relationshipSelected, '') = '' OR TYPE(d) = $relationshipSelected) AND
        (COALESCE($personSelected, '') = '' OR p.name = $personSelected) AND
        (COALESCE($movieSelected, '') = '' OR m.title = $movieSelected) AND
        (COALESCE($yearSelected, '') = '' OR m.released > $yearSelected)
  RETURN p, d, m
  ORDER BY CASE WHEN COALESCE($orderPersonSelected, '') = 'ASC' THEN p.name END ASC,
           CASE WHEN COALESCE($orderPersonSelected, '') = 'DESC' THEN p.name END DESC,
           CASE WHEN COALESCE($orderMovieSelected, '') = 'ASC' THEN m.title END ASC,
           CASE WHEN COALESCE($orderMovieSelected, '') = 'DESC' THEN m.title END DESC
`, {
    personSelected, movieSelected, relationshipSelected: reTraduccion(relationshipSelected),
    orderPersonSelected, orderMovieSelected,
    yearSelected: !!yearSelected ? parseInt(yearSelected) : null
  });

  const [moviesList, setMoviesList] = useState<any>([]);
  const [personsList, setPersonsList] = useState<any>([]);
  const [relationshipsList, setRelationshipsList] = useState<any>([]);
  const [yearsList, setYearsList] = useState<any>([]);
  const NODE_R = 8;

  const { loading: loadingPersons, result: resultPersons, error: errorPersons, run: runPersons } = useReadCypher(
    `MATCH (p:Person)-[d]->(m:Movie)
    WHERE(COALESCE($movieSelected, '') = '' OR m.title = $movieSelected)
    RETURN DISTINCT p
    ORDER BY p.name ASC`,
    { movieSelected }
  );

  const { loading: loadingMovies, result: resultMovies, run: runMovies } = useReadCypher(
    `MATCH (p:Person)-[d]->(m:Movie)
    WHERE (COALESCE($personSelected, '') = '' OR p.name = $personSelected)
    RETURN DISTINCT m
    ORDER BY m.title ASC`,
    { personSelected }
  );

  const { loading: loadingRelationships, result: resultRelationships } = useReadCypher(
    `MATCH ()-[r]->() RETURN COLLECT(DISTINCT TYPE(r)) AS tipos`
  );

  // const { loading, result: queryresult, error } = useReadCypher(query, {});

  // Memorizo `processInfo` para evitar renders infinitos
  const processInfo = useCallback((record: any) => {
    const person = record.get('p'); // Nodo `p` (Person)
    const relationship = record.get('d'); // Relación `d` (DIRECTED)
    const movie = record.get('m'); // Nodo `m` (Movie)

    return {
      Person: person?.properties?.name || 'Unknown',
      Relationship: traducirRelacion(relationship?.type) || 'Unknown',
      Movie: movie?.properties?.title || 'Unknown',
      Tagline: movie?.properties?.tagline || 'N/A',
      Released: movie?.properties?.released?.low || 'N/A',
    };
  }, []);
  let nodoId = 0;
  const processDataGraph = useCallback((info: any) => {
    const nodes: any = [];
    const links: any = [];
    let nodeIdPerson = 0;
    let nodeIdMovie = 0;
    info.forEach((item: any) => {
      const personNode = nodes.find((node: any) => node.name === item.Person && node.type === item.Relationship);
      if (personNode) {
        nodeIdPerson = personNode.id;
      } else {
        nodeIdPerson = nodoId++;
      }
      let color = 'blue';
      switch (item.Relationship) {
        case 'Actor/Actriz':
          color = '#3AC34CB7';
          break;
        case 'Dirigido/a':
          color = '#C33A3AB7';
          break;
        case 'Producido/a':
          color = '#AC3AC3B7';
          break;
        case 'Escritor':
          color = '#C3B83AB7';
          break;
        case 'Seguimientos':
          color = '#C3853AB7';
          break;
        case 'Revisiones':
          color = '#C33AAAB7';
          break;
        default:
          color = 'orange';
          break;
      }
      if (!personNode) {

        nodes.push({
          id: nodeIdPerson,
          name: item.Person,
          type: item.Relationship,
          val: 1,
          color
        });
      }

      const movieNode = nodes.find((node: any) => node.name === item.Movie);
      if (movieNode) {
        nodeIdMovie = movieNode.id;
      } else {
        nodeIdMovie = nodoId++;
      }
      if (!movieNode) {
        nodes.push({ id: nodeIdMovie, name: item.Movie, type: 'Movie', color: '#3A6FC3B7', val: 1.5 });
      }

      links.push({
        source: nodeIdPerson,
        target: nodeIdMovie,
        value: 1,
        relationship: item.Relationship,
        color
      });
    });
    return { nodes, links };
  }, [nodoId]);
  const onClickReset = () => {
    setPersonSelected('');
    setMovieSelected('');
    setRelationshipSelected('');
    setYearSelected('');
    setOrderMovieSelected('');
    setOrderPersonSelected('');
  }
  const processQueryToShow = () => {
    const initialQuery = `
MATCH (p:Person)-[d]->(m:Movie)${relationshipSelected
        ? "\nWHERE TYPE(d) = '" + relationshipSelected + "'" + (personSelected || movieSelected || yearSelected ? ' AND ' : '')
        : !personSelected && !movieSelected && !yearSelected ? '' : '\nWHERE '
      }${personSelected
        ? "p.name = '" + personSelected + "'" + (movieSelected || yearSelected ? " AND " : '')
        : ''}${movieSelected
          ? "m.title = '" + movieSelected + "'" + (yearSelected ? " AND " : '')
          : ''}${yearSelected
            ? "m.releated > " + yearSelected
            : ''}
RETURN p, d, m
${orderPersonSelected !== '' || orderMovieSelected !== ''
        ? 'ORDER BY '
        : ''}${orderPersonSelected !== '' ? orderPersonSelected === 'ASC'
          ? "p.name ASC" + (orderMovieSelected ? ', ' : '')
          : "p.name DESC" + (orderMovieSelected ? ', ' : '')
          : ''}${orderMovieSelected !== '' ? orderMovieSelected === 'ASC'
            ? "m.title ASC"
            : "m.title DESC"
            : ''}
    `
    return initialQuery
  }
  // Ejecuto `processInfo` cuando los datos estén disponibles
  useEffect(() => {
    // console.log("calculado", queryresult);
    console.log("result", queryresult?.records);
    if (!loading && queryresult) {
      const processed = queryresult.records.map((record: any) => processInfo(record));
      setRows(processed);
      console.log("processed", processed);

      const dataToGraph = processDataGraph(processed);
      console.log("dataToGraph", dataToGraph);
      setDataGraph(dataToGraph);

    }
    if (error) console.error('Error ejecutando la consulta:', error);
  }, [loading, queryresult, processInfo, error]);

  const renderNodesReferences = () => {
    return typesList.map((item: any) => {
      return (
        < div className="mr-2 mb-2" >
          <svg width="20" height="20">
            <circle cx="10" cy="10" r="10" fill={item.color} />
          </svg>
          <span className="ms-1">{item.relation}</span>
        </div >
      );
    })
  };
  const renderNodesRelations = () => {
    return typesList.map((item: any) => {
      if(item.relation === 'Pelicula') return null;
      return (
        < div className="mr-2 mb-2" >
          <svg width="20" height="5">
            <rect x="0" y="0" width="20" height="5" fill={item.color} />
          </svg>
          <span className="ms-1">{item.relation}</span>
        </div >
      );
    })
  };

  useEffect(() => {
    if (!loadingPersons && resultPersons) {
      const processed = resultPersons.records.map((record: any) => record.get('p').properties.name);
      setPersonsList(processed);
    }
    if (errorPersons) console.error('Error ejecutando la consulta:', errorPersons);
  }, [loadingPersons, resultPersons, errorPersons]);

  useEffect(() => {
    if (!loadingMovies && resultMovies) {
      const processed = resultMovies.records.map((record: any) => record.get('m').properties.title);
      setMoviesList(processed);
    }
  }, [loadingMovies, resultMovies])

  useEffect(() => {
    if (!loadingRelationships && resultRelationships) {
      const processed = resultRelationships.records[0].get('tipos');
      const relationsTraducidas = processed.map((item: any) => {
        return traducirRelacion(item);
      })
      setRelationshipsList(relationsTraducidas);
    }
  }, [loadingRelationships, resultRelationships])

  useEffect(() => {
    // console.log("seleccionado un campo entonces run", personSelected, movieSelected, relationshipSelected, yearSelected, orderPersonSelected, orderMovieSelected);
    run({
      personSelected, movieSelected, relationshipSelected: reTraduccion(relationshipSelected),
      orderMovieSelected, orderPersonSelected, yearSelected: !!yearSelected ? parseInt(yearSelected) : null
    });
    const query = processQueryToShow();
    setQueryShow(query);
  }, [personSelected, movieSelected, relationshipSelected, yearSelected, orderPersonSelected, orderMovieSelected]);

  useEffect(() => {
    runMovies({ personSelected });
    // if(movieSelected !== '') setMovieSelected('');
    // setMovieSelected('');
  }, [personSelected]);

  useEffect(() => {
    runPersons({ movieSelected });
    // if(personSelected !== '') setPersonSelected('');
    // setPersonSelected('');
  }, [movieSelected]);

  useEffect(() => {
    function getYears() {
      const years = [];
      for (let i = 2024; i >= 1980; i--) {
        years.push(i);
      }
      return years;
    }
    setYearsList(getYears());
  }, []);
  return (
    <div>

      <div className='d-flex flex-column align-items-center mt-3 p-3'>
        <h2 className="mb-2">Integración React con Neo4j</h2>
        <div className='w-100 d-flex flex-column align-items-center shadow-sm rounded bg-white p-4'
        
        >
          <div className="d-flex justify-content-around w-100">
            <div className="d-flex flex-column align-items-center">
              <div className="d-flex justify-content-between w-100 " style={{ gap: '1rem' }}>
                <div className="d-flex flex-column align-items-left">
                  <label htmlFor="person-select" className="mb-2">Persona</label>
                  <select id="person-select" className="form-control shadow-sm" style={{ appearance: 'menulist-button' }} value={personSelected} onChange={(e) => setPersonSelected(e.target.value)}>
                    <option key={'none'} value={''}>Todos</option>
                    {personsList.map((person: any, index: any) => (
                      <option key={index} value={person}>{person}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex flex-column align-items-left">
                  <label htmlFor="person-select" className="mb-2">Relación</label>
                  <select id="relationship-select" className="form-control shadow-sm" style={{ width: 110, appearance: 'menulist-button' }} value={relationshipSelected} onChange={(e) => setRelationshipSelected(e.target.value)}>
                    <option key={'none'} value={''}>Todos</option>
                    {relationshipsList.map((relationship: any, index: any) => (
                      <option key={index} value={relationship}>{relationship}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex flex-column align-items-left">
                  <label htmlFor="person-select" className="mb-2">Película</label>
                  <select id="movie-select" className="form-control shadow-sm" style={{ appearance: 'menulist-button' }} value={movieSelected} onChange={(e) => setMovieSelected(e.target.value)}>
                    <option key={'none'} value={''}>Todos</option>
                    {moviesList.map((movie: any, index: any) => (
                      <option key={index} value={movie}>{movie}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="d-flex justify-content-start w-100 mt-4" style={{ gap: '1rem' }}>
                <div className="d-flex flex-column align-items-left">
                  <label htmlFor="year-select" className="mb-2">Desde año</label>
                  <select id="year-select" className="form-control shadow-sm" style={{ width: 100, appearance: 'menulist-button' }} value={yearSelected} onChange={(e) => setYearSelected(e.target.value)}>
                    <option key={'none'} value={''}>Todos</option>
                    {yearsList.map((year: any, index: any) => (
                      <option key={index} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex flex-column align-items-left">
                  <label htmlFor="order-person-select" className="mb-2">Ordenar por Persona</label>
                  <select id="order-person-select" className="form-control shadow-sm" style={{ appearance: 'menulist-button' }} value={orderPersonSelected} onChange={(e) => setOrderPersonSelected(e.target.value)}>
                    <option key={'none'} value={''}>No</option>
                    <option key={'asc'} value={'ASC'}>Ascendente</option>
                    <option key={'desc'} value={'DESC'}>Descendente</option>
                  </select>
                </div>
                <div className="d-flex flex-column align-items-left">
                  <label htmlFor="order-movie-select" className="mb-2">Ordenar por Pelicula</label>
                  <select id="order-movie-select" className="form-control shadow-sm" style={{ appearance: 'menulist-button' }} value={orderMovieSelected} onChange={(e) => setOrderMovieSelected(e.target.value)}>
                    <option key={'none'} value={''}>No</option>
                    <option key={'asc'} value={'ASC'}>Ascendente</option>
                    <option key={'desc'} value={'DESC'}>Descendente</option>
                  </select>
                </div>
                <div className="d-flex flex-column align-items-left justify-content-start" style={{ height: '100%' }}>
                  <button className="btn btn-primary shadow-md d-flex justify-content-center align-items-center" style={{ width: '120px', height: '30px', marginTop: '27px', marginLeft: '10px' }}
                    onClick={onClickReset}>
                    Borrar Filtros
                  </button>
                </div>
              </div>
            </div>
            <div className="table-container d-flex justify-content-center align-items-start">
              <div className="d-flex justify-content-around" style={{ maxWidth: '600px', minWidth: '300px' }}>
                <div className="card shadow-sm p-3 bg-white rounded">
                  <h3 className="text-center">Consulta realizada</h3>
                  <hr className="mb-3 border-top border-secondary" />
                  <pre className="text-break" style={{ whiteSpace: 'pre-wrap' }}>
                    {queryShow}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="graphCointainer"
        className='d-flex flex-column align-items-center m-3 shadow-sm rounded bg-white p-2'
      >
        <h2 className="mb-2">Gráfico relacional</h2>
        <div className='w-100 d-flex flex-column-reverse flex-lg-row align-items-start justify-content-between'>
          <div className="m-3 shadow-sm rounded bg-white p-3 w-95">
          <div style={{ width: '150px' }}></div>
            <h5 className="mb-4"> Referencias </h5>
            <div className="d-flex flex-row flex-lg-column align-items-start mb-2 w-100">
              <h6 className="mb-2 text-decoration-underline me-3"> Tipos </h6>
              {renderNodesReferences()}
            </div>
            <div className="d-flex flex-row flex-lg-column align-items-start mb-2">
              <h6 className="mb-2 text-decoration-underline me-3" > Relaciones </h6>
              {renderNodesRelations()}
            </div>
          </div>
          <div className='d-flex flex-column align-items-center w-100'>
            <ForceGraph2D
              graphData={dataGraph}
              nodeLabel='name'
              linkLabel='relationship'
              // backgroundColor='#DFFDFFFF'
              width={800}
              height={500}
              linkWidth={3}
              nodeAutoColorBy={'type'}
              nodeCanvasObjectMode={() => "after"}
              nodeRelSize={NODE_R}
              nodeCanvasObject={(node: any, ctx: any, globalScale: any) => {
                const label = node.name;
                const fontSize = 12 / globalScale; // Ajusta el tamaño de la fuente según el zoom
                ctx.font = `${fontSize}px Sans-Serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillStyle = "Black"; // Puedes usar node.color para un color dinámico
                // Dibujar el texto en la posición del nodo
                if (node.type === 'Movie') {
                  const image = new Image();
                  image.src = '/Movie.png'; // o 'path/to/movie/image.svg'
                  const size = 10;
                  ctx.drawImage(image, node.x - size / 2, (node.y - size / 2) - 3, size, size);
                  ctx.fillText(label, node.x, node.y + 3);
                } else {
                  ctx.arc(node.x, node.y, 10, 0, 2 * Math.PI, false);
                  ctx.fillText(label, node.x, node.y);
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className='d-flex flex-column align-items-center m-3 shadow-sm rounded bg-white p-2'>
        <h2 className="mb-2 mt-2">Tabla de resultados</h2>
        <div className="table-container w-100 p-4 mt-0 d-flex justify-content-center align-items-start">
          <table className="table table-striped table-bordered shadow-sm">
            <thead>
              <tr>
                <th className="text-center">Persona</th>
                <th className="text-center">Relacion</th>
                <th className="text-center">
                  <div className="d-flex flex-column">
                    <div>Película</div>
                    <div className="d-flex justify-content-around border-top">
                      <span className="text-center mt-2" style={{ width: 200 }}>Titulo</span>
                      <span className="text-center mt-2" style={{ width: 200 }}>Lema</span>
                      <span className="text-center mt-2" style={{ width: 50 }}>Lanzamiento</span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? rows.map((row: any, index: any) => (
                <tr key={index}>
                  <td>{row.Person}</td>
                  <td>{row.Relationship}</td>
                  <td>
                    <div className="d-flex flex-column">
                      <div className="d-flex justify-content-around">
                        <span className="text-center" style={{ width: 200 }}>{row.Movie}</span>
                        <span className="text-center" style={{ width: 200 }}>{row.Tagline}</span>
                        <span className="text-center" style={{ width: 50 }}>{row.Released}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
                :
                <tr>
                  <td colSpan={3} style={{ height: 100 }}>
                    <div className="d-flex justify-content-center align-items-center h-100">
                      {loading ? (
                        <div className="spinner-border text-primary" role="status">
                          {/* <span className="sr-only">Cargando...</span> */}
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          Sin resultados
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ height: '10px' }} />
    </div>
  );
};

export default App;
